import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from '../../../components/ui/Card';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import { 
  Image, 
  Play, 
  ThumbsUp, 
  MessageCircle, 
  Share2, 
  TrendingUp,
  Eye,
  MousePointerClick,
  ShoppingCart,
  BarChart3,
  RefreshCw,
  Filter,
  SlidersHorizontal,
  BarChart,
  LineChart,
  ArrowUp,
  ArrowDown,
  Loader2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Mail,
  Send
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label
} from 'recharts';

const CREATIVE_EMAIL_BRAND_IDS = [
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7',
  'dfcf54dd-b00d-434a-be1e-27b6484848eb',
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f',
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e',
  '526a6404-f685-44cd-b57f-27d692845b82',
  'c7778265-8ac6-4697-ae89-2f4da315d0ac',
];

export default function MetaCreativePerformance() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const isMarketer = selectedBrand?.currentUserRole === 'MARKETER';
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [selectedCampaignKey, setSelectedCampaignKey] = useState(null);
  const [selectedAdGroupKey, setSelectedAdGroupKey] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [campaignSort, setCampaignSort] = useState({ key: 'spend', dir: 'desc' });
  const [listViewMode, setListViewMode] = useState(isMarketer ? 'creativeIds' : 'campaigns'); // 'campaigns' | 'creativeIds'
  const [creativeIdSort, setCreativeIdSort] = useState({ key: 'spend', dir: 'desc' });
  const [selectedCreativeIdKey, setSelectedCreativeIdKey] = useState(null);
  
  // Email report state
  const [reportEmail, setReportEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailDatePreset, setEmailDatePreset] = useState('last30');
  const [emailCustomStart, setEmailCustomStart] = useState(null);
  const [emailCustomEnd, setEmailCustomEnd] = useState(null);
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [metaAccountFilter, setMetaAccountFilter] = useState('all');
  const [creatives, setCreatives] = useState([]);
  const [metaCreativePerformanceData, setMetaCreativePerformanceData] = useState([]);
  const [creativeDetailsData, setCreativeDetailsData] = useState([]);
  const [creativeDetailsMap, setCreativeDetailsMap] = useState({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [visibleThumbCount, setVisibleThumbCount] = useState(20);
  const [previewCache, setPreviewCache] = useState({});
  const previewContainerRef = useRef(null);
  const FALLBACK_IMG =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAn0B9z0Q3CYAAAAASUVORK5CYII=';
  
  // Video iframesi için referans
  const videoIframeRef = useRef(null);

  // Account filter options from creative data
  const accountNameMap = useMemo(() => {
    const map = {};
    metaCreativePerformanceData.forEach(r => {
      if (r.account_id && r.account_name) map[r.account_id] = r.account_name;
    });
    return map;
  }, [metaCreativePerformanceData]);

  const metaAccountOptions = useMemo(() => {
    const ids = new Set();
    metaCreativePerformanceData.forEach(r => { if (r.account_id) ids.add(r.account_id); });
    return Array.from(ids).sort();
  }, [metaCreativePerformanceData]);

  const filteredCreativeData = useMemo(() => {
    if (metaAccountFilter === 'all') return metaCreativePerformanceData;
    return metaCreativePerformanceData.filter(r => r.account_id === metaAccountFilter);
  }, [metaCreativePerformanceData, metaAccountFilter]);

  // Brand-specific dummy fallback id
  const DUMMY_BRAND_ID = '9ad536a0-dbdf-4935-8730-f2c2029c6d52';
  const DEMO_BRAND_ID_SC = '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a';
  const SPECIAL_DUMMY_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

  // Build dummy creatives and raw performance rows
  const buildDummyMetaData = () => {
    // Real fashion/e-commerce style images from Unsplash
    const dummyCreatives = [
      {
        creative_id: 'MC-001',
        name: 'Kış Koleksiyonu - Carousel',
        title: 'Yeni Sezon Kış Koleksiyonu',
        body: '❄️ Kış sezonuna özel %40\'a varan indirimler! Sıcacık kalın, şık görünün. Hemen keşfedin 👉',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=600&fit=crop',
        ad_name: 'IG Feed - Kış Carousel',
        campaign_id: 'CMP-001',
        campaign_name: 'Kış Kampanyası 2024',
        adset_id: 'AS-001',
        adset_name: 'LAL - Kadın 25-44',
        spend: 18500, clicks: 12400, impressions: 485000, purchases: 890, revenue: 156000
      },
      {
        creative_id: 'MC-002',
        name: 'Kadın Mont - Single Image',
        title: 'Premium Mont Koleksiyonu',
        body: '🧥 Bu kışın en trend montları şimdi stoklarda! Ücretsiz kargo + Kolay iade. #KışModası',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1539533113208-f6df8cc8b543?w=600&h=600&fit=crop',
        ad_name: 'IG Stories - Mont Promo',
        campaign_id: 'CMP-001',
        campaign_name: 'Kış Kampanyası 2024',
        adset_id: 'AS-002',
        adset_name: 'INT - Fashion Lovers',
        spend: 14200, clicks: 9800, impressions: 320000, purchases: 645, revenue: 98000
      },
      {
        creative_id: 'MC-003',
        name: 'Erkek Casual - Video',
        title: 'Erkek Günlük Giyim',
        body: '👔 Şıklık her an seninle! Erkek casual koleksiyonumuz ile tarzını yansıt. %30 İndirim 🎉',
        is_video: true,
        platform: 'facebook',
        image_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop',
        ad_name: 'FB Feed - Erkek Video',
        campaign_id: 'CMP-002',
        campaign_name: 'Erkek Koleksiyonu',
        adset_id: 'AS-003',
        adset_name: 'LAL - Erkek 18-35',
        spend: 12800, clicks: 8500, impressions: 280000, purchases: 520, revenue: 78500
      },
      {
        creative_id: 'MC-004',
        name: 'Çocuk Giyim - Collection',
        title: 'Minikler İçin Özel',
        body: '👶 Çocuklarınız için en kaliteli ve rahat kıyafetler! Organik kumaş, güvenli boyalar. Anne babalar için %25 indirim!',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1503919545889-aef636e10ad4?w=600&h=600&fit=crop',
        ad_name: 'IG Reels - Çocuk Tanıtım',
        campaign_id: 'CMP-003',
        campaign_name: 'Çocuk Koleksiyonu',
        adset_id: 'AS-004',
        adset_name: 'Parents - Çocuklu Ebeveynler',
        spend: 9500, clicks: 6200, impressions: 195000, purchases: 380, revenue: 52000
      },
      {
        creative_id: 'MC-005',
        name: 'Yılbaşı Özel - DPA',
        title: '🎄 Yılbaşı Hediyeleri',
        body: '🎁 Sevdiklerinize en güzel hediyeler! Yılbaşına özel %50\'ye varan indirimler. Son 3 gün!',
        is_video: false,
        platform: 'facebook',
        image_url: 'https://images.unsplash.com/photo-1512909006721-3d6018887383?w=600&h=600&fit=crop',
        ad_name: 'FB DPA - Yılbaşı Retargeting',
        campaign_id: 'CMP-004',
        campaign_name: 'Yılbaşı Kampanyası',
        adset_id: 'AS-005',
        adset_name: 'RET - Website Visitors',
        spend: 22000, clicks: 15800, impressions: 520000, purchases: 1250, revenue: 245000
      },
      {
        creative_id: 'MC-006',
        name: 'Spor Giyim - Video',
        title: 'Aktif Yaşam Koleksiyonu',
        body: '🏃‍♀️ Hareket özgürlüğü! Yeni spor koleksiyonumuzla performansınızı artırın. #AktifYaşa',
        is_video: true,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=600&fit=crop',
        ad_name: 'IG Reels - Spor Video',
        campaign_id: 'CMP-005',
        campaign_name: 'Spor Koleksiyonu',
        adset_id: 'AS-006',
        adset_name: 'INT - Fitness Enthusiasts',
        spend: 11500, clicks: 7800, impressions: 245000, purchases: 420, revenue: 68000
      },
      {
        creative_id: 'MC-007',
        name: 'Aksesuar - Carousel',
        title: 'Tarzını Tamamla',
        body: '👜 Çanta, şal, takı... Her detay önemli! Aksesuar koleksiyonumuzla stilinizi tamamlayın.',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600&h=600&fit=crop',
        ad_name: 'IG Feed - Aksesuar Carousel',
        campaign_id: 'CMP-006',
        campaign_name: 'Aksesuar Kampanyası',
        adset_id: 'AS-007',
        adset_name: 'LAL - Purchasers 1%',
        spend: 8200, clicks: 5400, impressions: 168000, purchases: 310, revenue: 42000
      },
      {
        creative_id: 'MC-008',
        name: 'Outlet - Flash Sale',
        title: '⚡ Flash Sale - 72 Saat',
        body: '🔥 Son fırsat! Outlet ürünlerinde ekstra %30 indirim. Stoklar tükenmeden alın!',
        is_video: false,
        platform: 'facebook',
        image_url: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=600&h=600&fit=crop',
        ad_name: 'FB Feed - Outlet Flash Sale',
        campaign_id: 'CMP-007',
        campaign_name: 'Outlet Kampanyası',
        adset_id: 'AS-008',
        adset_name: 'RET - Cart Abandoners',
        spend: 16800, clicks: 11200, impressions: 385000, purchases: 780, revenue: 125000
      },
      {
        creative_id: 'MC-009',
        name: 'Yeni Gelenler - Story',
        title: 'Yeni Sezon Ürünleri',
        body: '✨ Her hafta yeni ürünler! İlk siz görün, ilk siz alın. Takipte kalın 💫',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&h=600&fit=crop',
        ad_name: 'IG Stories - Yeni Gelenler',
        campaign_id: 'CMP-008',
        campaign_name: 'Yeni Sezon Lansman',
        adset_id: 'AS-009',
        adset_name: 'INT - Engaged Shoppers',
        spend: 7500, clicks: 4800, impressions: 142000, purchases: 265, revenue: 38500
      },
      {
        creative_id: 'MC-010',
        name: 'Brand Awareness - Video',
        title: 'Hikayemiz',
        body: '💚 25 yıldır Türkiye\'nin en sevilen markası. Kalite, güven, stil... Bizimle tanışın.',
        is_video: true,
        platform: 'facebook',
        image_url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=600&fit=crop',
        ad_name: 'FB Video - Brand Story',
        campaign_id: 'CMP-009',
        campaign_name: 'Brand Awareness',
        adset_id: 'AS-010',
        adset_name: 'INT - Broad Audience TR',
        spend: 25000, clicks: 8500, impressions: 850000, purchases: 180, revenue: 32000
      },
      {
        creative_id: 'MC-011',
        name: 'Ayakkabı - Single Image',
        title: 'Adımlarınıza Şıklık',
        body: '👟 Yeni sezon ayakkabı koleksiyonu! Konfor ve stil bir arada. %35 indirim fırsatı!',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&h=600&fit=crop',
        ad_name: 'IG Feed - Ayakkabı Promo',
        campaign_id: 'CMP-010',
        campaign_name: 'Ayakkabı Kampanyası',
        adset_id: 'AS-011',
        adset_name: 'LAL - Shoe Buyers',
        spend: 13500, clicks: 9100, impressions: 295000, purchases: 520, revenue: 89000
      },
      {
        creative_id: 'MC-012',
        name: 'Triko - Cozy Collection',
        title: 'Sıcacık Trikolar',
        body: '🧶 El yapımı görünümlü, fabrika kalitesinde trikolar. Yumuşak dokunuş, sıcak kış.',
        is_video: false,
        platform: 'instagram',
        image_url: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600&h=600&fit=crop',
        ad_name: 'IG Stories - Triko Cozy',
        campaign_id: 'CMP-001',
        campaign_name: 'Kış Kampanyası 2024',
        adset_id: 'AS-001',
        adset_name: 'LAL - Kadın 25-44',
        spend: 10200, clicks: 6800, impressions: 215000, purchases: 385, revenue: 58000
      }
    ];

    const today = new Date();
    const days = 30;
    const metaRaw = [];

    // Generate daily performance for each creative
    dummyCreatives.forEach(creative => {
      for (let d = 0; d < days; d++) {
        const date = new Date(today);
        date.setDate(today.getDate() - (days - 1 - d));
        const factor = 0.7 + Math.random() * 0.6; // Random daily variance
        
        metaRaw.push({
          creative_id: creative.creative_id,
          date: date.toISOString().slice(0, 10),
          spend: Math.round((creative.spend / days) * factor),
          clicks: Math.round((creative.clicks / days) * factor),
          impressions: Math.round((creative.impressions / days) * factor),
          purchases: Math.round((creative.purchases / days) * factor),
          revenue: Math.round((creative.revenue / days) * factor),
          conversions: Math.round((creative.purchases / days) * factor),
          conversion_value: Math.round((creative.revenue / days) * factor),
          campaign_id: creative.campaign_id,
          campaign_name: creative.campaign_name,
          adset_id: creative.adset_id,
          adset_name: creative.adset_name,
          ad_id: `AD-${creative.creative_id}`,
          ad_name: creative.ad_name
        });
      }
    });

    // Convert to creatives list format
    const creativesList = dummyCreatives.map((c, i) => ({
      id: `meta-${i + 1}`,
      creative_id: c.creative_id,
      name: c.name,
      title: c.title,
      body: c.body,
      is_video: c.is_video,
      platform: c.platform,
      spend: c.spend,
      clicks: c.clicks,
      impressions: c.impressions,
      purchases: c.purchases,
      revenue: c.revenue,
      image_url: c.image_url,
      thumbnail_url: c.image_url,
      best_media_url: c.image_url,
      object_type: c.is_video ? 'VIDEO' : 'IMAGE',
      ad_id: `AD-${c.creative_id}`,
      ad_name: c.ad_name,
      campaign_id: c.campaign_id,
      campaign_name: c.campaign_name,
      campaignKey: c.campaign_id,
      adGroupId: c.adset_id,
      adGroupName: c.adset_name,
      adGroupKey: c.adset_id
    }));

    return { creativesList, metaRaw };
  };

  // NOTE: DPA (Dynamic Product Ads) creatives always have `{{product.name}}`
  // placeholders in their name/title/body because Meta fills them per-product at
  // render time. Meta's preview iframe + thumbnail_url endpoints resolve these
  // placeholders correctly, so we no longer filter template creatives out — that
  // was hiding entire campaigns (e.g. FB/CO/DPA/ON/RM) from this page.
  const hasTemplateVariables = () => false;

  // Seçilen creative için performans verilerini hazırla
  const getPerformanceData = (creativeId) => {
    if (!filteredCreativeData || filteredCreativeData.length === 0 || !creativeId) {
      return [];
    }

    // Seçilen creative için verileri filtrele
    const filteredData = filteredCreativeData.filter(
      item => item.creative_id === creativeId
    );
    
    // Tarih bazlı verileri grupla
    const groupedByDate = {};
    
    filteredData.forEach(item => {
      const date = item.date ? new Date(item.date).toISOString().split('T')[0] : null;
      if (!date) return;
      
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          date,
          spend: 0,
          clicks: 0,
          impressions: 0,
          purchases: 0,
          revenue: 0
        };
      }
      
      groupedByDate[date].spend += Number(item.spend || 0);
      groupedByDate[date].clicks += Number(item.clicks || 0);
      groupedByDate[date].impressions += Number(item.impressions || 0);
      groupedByDate[date].purchases += Number(item.purchases || 0);
      groupedByDate[date].revenue += Number(item.revenue || item.conversion_value || 0);
    });
    
    // Objeyi diziye çevir ve tarihe göre sırala
    const performanceData = Object.values(groupedByDate).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    
    // Hesaplanmış değerleri ekle
    performanceData.forEach(day => {
      // CTR: Tıklama Oranı
      day.ctr = day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0;
      
      // CPC: Tıklama Başına Maliyet
      day.cpc = day.clicks > 0 ? day.spend / day.clicks : 0;
      
      // ROAS: Return on Ad Spend
      day.roas = day.spend > 0 ? day.revenue / day.spend : 0;
    });
    
    return performanceData;
  };

  // Video iframe yüklendiğinde ses seviyesini ayarlama
  useEffect(() => {
    // iframe yüklendikten sonra ses seviyesini ayarlamaya çalışalım
    const adjustVideoVolume = () => {
      if (videoIframeRef.current) {
        try {
          // Iframe yüklendikten sonra postMessage ile sesi tamamen kapalı yapalım
          const message = {
            method: 'setVolume',
            value: 0  // Ses kapalı
          };
          
          // iframe'in contentWindow'una mesaj gönderelim
          videoIframeRef.current.contentWindow.postMessage(JSON.stringify(message), '*');
          console.log('Video sesi kapatıldı');
          
        } catch (error) {
          console.error('Video ses ayarı yapılamadı:', error);
        }
      }
    };
    
    // iframe yüklendikten sonra ses ayarlamayı çalıştır
    const timer = setTimeout(adjustVideoVolume, 1000);
    
    return () => clearTimeout(timer);
  }, [selectedCreative]);

  // Helper: prefetch preview for a list of creative ids (batch)
  const prefetchPreviews = async (ids) => {
    try {
      if (!ids || ids.length === 0) return;
      const brandId = localStorage.getItem('currentBrandId');
      const uncached = ids.filter(id => !previewCache[id]);
      if (uncached.length === 0) return;
      const chunkSize = 20;
      for (let i = 0; i < uncached.length; i += chunkSize) {
        const chunk = uncached.slice(i, i + chunkSize);
        const resp = await axios.get(`https://api.glowytics.com/api/meta-ads/live/creative-preview`, {
          params: { creativeIds: chunk.join(','), adFormat: 'MOBILE_FEED_STANDARD' },
          withCredentials: true,
          headers: { 'X-Brand-ID': brandId }
        });
        const rows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
        if (rows.length) {
          setPreviewCache((prev) => {
            const next = { ...prev };
            rows.forEach(r => {
              if (r?.creative_id && r?.body) next[r.creative_id] = r.body;
            });
            return next;
          });
        }
      }
    } catch (e) {
      // ignore prefetch errors silently
    }
  };

  // Generate dummy Instagram-style post preview HTML
  const generateDummyPreviewHtml = (creative) => {
    if (!creative) return '';
    
    const platform = (creative.platform || 'instagram').toLowerCase();
    const isInstagram = platform === 'instagram';
    const brandName = 'Fashion Brand';
    const brandHandle = '@fashionbrand';
    
    return `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 470px; margin: 0 auto; background: #fff; border: 1px solid #dbdbdb; border-radius: 8px; overflow: hidden;">
        <!-- Header -->
        <div style="display: flex; align-items: center; padding: 14px 16px; border-bottom: 1px solid #efefef;">
          <div style="width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%); display: flex; align-items: center; justify-content: center; margin-right: 12px;">
            <div style="width: 28px; height: 28px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center;">
              <span style="font-size: 12px; font-weight: 600; color: #262626;">FB</span>
            </div>
          </div>
          <div style="flex: 1;">
            <div style="font-weight: 600; font-size: 14px; color: #262626;">${brandName}</div>
            <div style="font-size: 12px; color: #8e8e8e;">Sponsored</div>
          </div>
          <div style="color: #262626; font-size: 16px;">•••</div>
        </div>
        
        <!-- Image -->
        <div style="position: relative; width: 100%; aspect-ratio: 1/1; background: #fafafa;">
          <img src="${creative.image_url || creative.best_media_url || creative.thumbnail_url}" 
               style="width: 100%; height: 100%; object-fit: cover;" 
               alt="${creative.title || 'Ad creative'}"
               onerror="this.src='https://via.placeholder.com/470x470/f0f0f0/999?text=Preview'"
          />
          ${creative.is_video ? `
            <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 60px; height: 60px; background: rgba(0,0,0,0.6); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
              <div style="width: 0; height: 0; border-left: 20px solid #fff; border-top: 12px solid transparent; border-bottom: 12px solid transparent; margin-left: 4px;"></div>
            </div>
          ` : ''}
        </div>
        
        <!-- Action buttons -->
        <div style="padding: 12px 16px;">
          <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 12px;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#262626" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#262626" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#262626" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            <div style="flex: 1;"></div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#262626" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
          </div>
          
          <div style="font-weight: 600; font-size: 14px; color: #262626; margin-bottom: 8px;">
            ${Math.floor(creative.clicks / 100).toLocaleString()} likes
          </div>
          
          <div style="font-size: 14px; color: #262626; line-height: 1.5;">
            <span style="font-weight: 600;">${brandHandle}</span> ${creative.body || creative.title || 'Check out our latest collection!'}
          </div>
          
          <div style="margin-top: 12px;">
            <a href="#" style="display: inline-block; background: #0095f6; color: #fff; padding: 8px 16px; border-radius: 8px; font-size: 14px; font-weight: 600; text-decoration: none;">
              Shop Now
            </a>
          </div>
          
          <div style="margin-top: 12px; font-size: 10px; color: #8e8e8e; text-transform: uppercase;">
            ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>
    `;
  };

  // Fetch Meta ad preview HTML for the selected creative (uses cache if available)
  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setPreviewLoading(true);
        setPreviewHtml('');
        const cid = selectedCreative?.creative_id || selectedCreative?.id;
        const brandId = localStorage.getItem('currentBrandId');
        if (!cid || !brandId) {
          setPreviewLoading(false);
          return;
        }
        
        // For dummy brands, generate preview HTML locally
        if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC || brandId === SPECIAL_DUMMY_BRAND_ID) {
          const dummyHtml = generateDummyPreviewHtml(selectedCreative);
          setPreviewHtml(dummyHtml);
          setPreviewLoading(false);
          return;
        }
        
        // Use cache if present
        if (previewCache[cid]) {
          setPreviewHtml(previewCache[cid]);
          setPreviewLoading(false);
          return;
        }
        const resp = await axios.get(`https://api.glowytics.com/api/meta-ads/live/creative-preview`, {
          params: { creativeIds: cid, adFormat: 'MOBILE_FEED_STANDARD' },
          withCredentials: true,
          headers: { 'X-Brand-ID': brandId }
        });
        const body = Array.isArray(resp?.data?.rows) && resp.data.rows.length ? resp.data.rows[0]?.body || '' : '';
        setPreviewHtml(body);
        if (body) {
          setPreviewCache((prev) => ({ ...prev, [cid]: body }));
        }
      } catch (e) {
        console.error('creative preview fetch error', e);
        setPreviewHtml('');
      } finally {
        setPreviewLoading(false);
      }
    };
    fetchPreview();
  }, [selectedCreative, previewCache]);

  // Auto-trim preview container: observe rendered iframe and shrink wrapper to actual content
  useEffect(() => {
    if (!previewHtml || previewLoading) return;
    const container = previewContainerRef.current;
    if (!container) return;

    const trimHeight = () => {
      const iframe = container.querySelector('iframe');
      if (!iframe) return;
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body) {
          const h = doc.body.scrollHeight || doc.documentElement.scrollHeight;
          if (h > 50) {
            container.style.height = h + 'px';
            iframe.style.height = h + 'px';
            return;
          }
        }
      } catch (_) { /* cross-origin */ }
      // Cross-origin fallback: scan visible pixels via iframe's rendered height
      // Use MutationObserver to detect when iframe body settles
      container.style.height = 'auto';
    };

    const iframe = container.querySelector('iframe');
    if (iframe) {
      iframe.addEventListener('load', trimHeight);
    }
    const t1 = setTimeout(trimHeight, 2000);
    const t2 = setTimeout(trimHeight, 4000);
    return () => {
      if (iframe) iframe.removeEventListener('load', trimHeight);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [previewHtml, previewLoading]);

  // After creatives are loaded, prefetch first 20 previews
  useEffect(() => {
    if (!creatives || creatives.length === 0) return;
    const ids = creatives
      .filter(c => !hasTemplateVariables(c))
      .slice(0, 20)
      .map(c => c.creative_id)
      .filter(Boolean);
    prefetchPreviews(ids);
  }, [creatives]);

  // When visible batch increases (scroll), prefetch previews for new batch
  useEffect(() => {
    if (!creatives || creatives.length === 0) return;
    const ids = creatives
      .filter(c => !hasTemplateVariables(c))
      .slice(0, visibleThumbCount)
      .map(c => c.creative_id)
      .filter(Boolean);
    prefetchPreviews(ids);
  }, [visibleThumbCount]);

  // Seçilen creative için performans verileri
  const performanceData = useMemo(() => {
    return selectedCreative ? getPerformanceData(selectedCreative.creative_id) : [];
  }, [selectedCreative, filteredCreativeData]);

  // Trend chart serisi: seçili aralık 30 günden uzunsa AYLIK kümelenir, değilse günlük kalır.
  const chartSeries = useMemo(() => {
    const pd = Array.isArray(performanceData) ? performanceData : [];
    if (pd.length === 0) return { data: [], monthly: false };
    const first = new Date(pd[0].date);
    const last = new Date(pd[pd.length - 1].date);
    const spanDays = Math.abs((last - first) / 86400000);
    if (spanDays <= 31) return { data: pd, monthly: false };
    const m = {};
    for (const d of pd) {
      const dt = new Date(d.date);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
      if (!m[key]) m[key] = { date: key, spend: 0, revenue: 0, clicks: 0, impressions: 0 };
      m[key].spend += Number(d.spend || 0);
      m[key].revenue += Number(d.revenue || 0);
      m[key].clicks += Number(d.clicks || 0);
      m[key].impressions += Number(d.impressions || 0);
    }
    const data = Object.values(m)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((x) => ({ ...x, roas: x.spend > 0 ? x.revenue / x.spend : 0 }));
    return { data, monthly: true };
  }, [performanceData]);

  // Campaign list aggregated from the same view rows (meta_ad_performance_with_creative_vw)
  const campaignList = useMemo(() => {
    if (!Array.isArray(filteredCreativeData) || filteredCreativeData.length === 0) return [];
    const map = new Map();
    filteredCreativeData.forEach(r => {
      const key = r.campaign_id || r.campaign_name || 'unknown';
      const name = r.campaign_name || 'Unnamed campaign';
      const agg = map.get(key) || {
        campaignId: r.campaign_id,
        campaignName: name,
        spend: 0,
        revenue: 0,
        clicks: 0,
        impressions: 0,
        purchases: 0
      };
      agg.spend += Number(r.spend || r.cost || 0);
      agg.revenue += Number(r.revenue || r.conversion_value || 0);
      agg.clicks += Number(r.clicks || 0);
      agg.impressions += Number(r.impressions || 0);
      agg.purchases += Number(r.purchases || r.conversions || 0);
      map.set(key, agg);
    });
    return Array.from(map.values()).sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));
  }, [filteredCreativeData]);

  const sortedCampaigns = useMemo(() => {
    const data = [...campaignList];
    const { key, dir } = campaignSort;
    const getVal = (c) => {
      if (key === 'roas') {
        return Number(c.spend || 0) > 0 ? Number(c.revenue || 0) / Number(c.spend || 0) : 0;
      }
      return Number(c[key] || 0);
    };
    data.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va === vb) return 0;
      return dir === 'asc' ? va - vb : vb - va;
    });
    return data;
  }, [campaignList, campaignSort]);

  const handleCampaignSort = (key) => {
    setCampaignSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'desc' };
    });
  };

  // Default select first campaign when available
  useEffect(() => {
    if (!selectedCampaignKey && campaignList.length > 0) {
      const first = campaignList[0];
      setSelectedCampaignKey(first.campaignId || first.campaignName || 'unknown');
    }
  }, [campaignList, selectedCampaignKey]);

  // Ad group list per campaign (aggregated)
  const adGroupByCampaign = useMemo(() => {
    if (!Array.isArray(filteredCreativeData) || filteredCreativeData.length === 0) return {};
    const map = {};
    filteredCreativeData.forEach(r => {
      const cKey = r.campaign_id || r.campaign_name || 'unknown';
      const agKey = r.adset_id || r.adset_name || null;
      if (!agKey) return;
      if (!map[cKey]) map[cKey] = new Map();
      const name = r.adset_name || 'Unnamed ad group';
      const agg = map[cKey].get(agKey) || {
        adGroupKey: agKey,
        adGroupName: name,
        spend: 0,
        revenue: 0,
        clicks: 0,
        impressions: 0,
        purchases: 0
      };
      agg.spend += Number(r.spend || r.cost || 0);
      agg.revenue += Number(r.revenue || r.conversion_value || 0);
      agg.clicks += Number(r.clicks || 0);
      agg.impressions += Number(r.impressions || 0);
      agg.purchases += Number(r.purchases || r.conversions || 0);
      map[cKey].set(agKey, agg);
    });
    const out = {};
    Object.keys(map).forEach(k => {
      out[k] = Array.from(map[k].values()).sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));
    });
    return out;
  }, [filteredCreativeData]);

  // Default select first ad group when campaign changes
  useEffect(() => {
    if (!selectedCampaignKey) {
      setSelectedAdGroupKey(null);
      return;
    }
    const list = adGroupByCampaign[selectedCampaignKey];
    if (list && list.length > 0) {
      if (!selectedAdGroupKey || !list.find(l => l.adGroupKey === selectedAdGroupKey)) {
        setSelectedAdGroupKey(list[0].adGroupKey);
      }
    } else {
      setSelectedAdGroupKey(null);
    }
  }, [selectedCampaignKey, adGroupByCampaign, selectedAdGroupKey]);

  // Creative ID list aggregated from view rows
  const creativeIdList = useMemo(() => {
    if (!Array.isArray(filteredCreativeData) || filteredCreativeData.length === 0) return [];
    const map = new Map();
    filteredCreativeData.forEach(r => {
      const key = r.creative_id || 'unknown';
      const agg = map.get(key) || {
        creativeId: r.creative_id,
        spend: 0,
        revenue: 0,
        clicks: 0,
        impressions: 0,
        purchases: 0
      };
      agg.spend += Number(r.spend || r.cost || 0);
      agg.revenue += Number(r.revenue || r.conversion_value || 0);
      agg.clicks += Number(r.clicks || 0);
      agg.impressions += Number(r.impressions || 0);
      agg.purchases += Number(r.purchases || r.conversions || 0);
      map.set(key, agg);
    });
    return Array.from(map.values()).sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));
  }, [filteredCreativeData]);

  const sortedCreativeIds = useMemo(() => {
    const data = [...creativeIdList];
    const { key, dir } = creativeIdSort;
    const getVal = (c) => {
      if (key === 'roas') {
        return Number(c.spend || 0) > 0 ? Number(c.revenue || 0) / Number(c.spend || 0) : 0;
      }
      return Number(c[key] || 0);
    };
    data.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va === vb) return 0;
      return dir === 'asc' ? va - vb : vb - va;
    });
    return data;
  }, [creativeIdList, creativeIdSort]);

  const handleCreativeIdSort = (key) => {
    setCreativeIdSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'desc' };
    });
  };

  // Marketer: force Creative IDs view
  useEffect(() => {
    if (isMarketer && listViewMode !== 'creativeIds') {
      setListViewMode('creativeIds');
      setSelectedCampaignKey(null);
      setSelectedAdGroupKey(null);
    }
  }, [isMarketer]);

  // Default select first creative id when switching to creativeIds view
  useEffect(() => {
    if (listViewMode === 'creativeIds' && !selectedCreativeIdKey && creativeIdList.length > 0) {
      setSelectedCreativeIdKey(creativeIdList[0].creativeId);
    }
  }, [listViewMode, creativeIdList, selectedCreativeIdKey]);

  // Filter creatives by selected campaign/adId + platform/format filters
  const filteredCreatives = useMemo(() => {
    if (listViewMode === 'creativeIds') {
      if (!selectedCreativeIdKey) return [];
      return creatives.filter(creative => {
        if (!creative) return false;
        if (creative.creative_id !== selectedCreativeIdKey) return false;
        if (hasTemplateVariables(creative)) return false;
        if (selectedPlatform !== 'all' && (creative.platform || '').toLowerCase() !== selectedPlatform) return false;
        if (selectedFormat !== 'all') {
          const isVideo = !!creative.is_video;
          if (selectedFormat === 'video' && !isVideo) return false;
          if (selectedFormat === 'single' && isVideo) return false;
        }
        return true;
      });
    }
    if (!selectedCampaignKey) return [];
    return creatives
      .filter(creative => {
        if (!creative || !creative.campaignKey) return false;
        if (creative.campaignKey !== selectedCampaignKey) return false;
        if (selectedAdGroupKey && creative.adGroupKey !== selectedAdGroupKey) return false;
        if (hasTemplateVariables(creative)) return false;
        if (selectedPlatform !== 'all' && (creative.platform || '').toLowerCase() !== selectedPlatform) return false;
        if (selectedFormat !== 'all') {
          const isVideo = !!creative.is_video;
          if (selectedFormat === 'video' && !isVideo) return false;
          if (selectedFormat === 'single' && isVideo) return false;
        }
        return true;
      });
  }, [creatives, selectedCampaignKey, selectedAdGroupKey, selectedPlatform, selectedFormat, listViewMode, selectedCreativeIdKey]);

  // When campaign changes, reset selected creative to first of filtered
  useEffect(() => {
    if (!filteredCreatives || filteredCreatives.length === 0) {
      setSelectedCreative(null);
      return;
    }
    setSelectedCreative(filteredCreatives[0]);
  }, [filteredCreatives]);

  useEffect(() => {
    // API'dan creative details ve meta creative performance verilerini çek
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get the active brand ID from local storage
        const brandId = localStorage.getItem('currentBrandId');
        
        if (!brandId) {
          console.error('No active brand ID found in localStorage');
          setLoading(false);
          return;
        }
        // Brand-specific dummy hydrate (always show dummy for this brand)
        if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC || brandId === SPECIAL_DUMMY_BRAND_ID) {
          const dummy = buildDummyMetaData();
          setMetaCreativePerformanceData(dummy.metaRaw);
          setCreatives(dummy.creativesList);
          setSelectedCreative(dummy.creativesList[0] || null);
          setLoading(false);
          return;
        }
        
        // custom seçili ise iki tarih de yoksa bekle
        if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
          setLoading(false);
          return;
        }
        console.log(`Fetching data for brand ID: ${brandId} with date range: ${dateRange}`);
        
        // Yeni endpoint üzerinden creative bazlı performansı çek
        try {
          const qs = (() => {
            if (dateRange === 'custom') {
              const s = new Date(customStartDate);
              const e = new Date(customEndDate);
              const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              return `?dateRange=custom&startDate=${fmt(s)}&endDate=${fmt(e)}`;
            }
            return `?dateRange=${dateRange}`;
          })();

          const resp = await axios.get(`https://api.glowytics.com/api/google-ads/meta-ad-performance-with-creatives${qs}`, {
            withCredentials: true,
            headers: {
              'X-Brand-ID': brandId
            }
          });

          const rows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
          console.log(`Retrieved ${rows.length} rows from meta_ad_performance_with_creative_vw`);

          // Yeni veriyi state'e yaz
          setMetaCreativePerformanceData(rows);
          // rows'a göre creative detaylarını meta_v2_creative_detail_raw'dan çek
          try {
            // Her creative_id için en güncel ad bilgilerini tut
            const latestByCreative = {};
            for (const r of rows) {
              const cid = r?.creative_id;
              if (!cid) continue;
              const current = latestByCreative[cid];
              const rTime = r?.date ? new Date(r.date).getTime() : 0;
              const cTime = current?.date ? new Date(current.date).getTime() : -1;
              if (!current || rTime >= cTime) {
                latestByCreative[cid] = r;
              }
            }
            const ids = Array.from(new Set(rows.map(r => r.creative_id).filter(Boolean)));
            const chunkSize = 200;
            const chunks = [];
            for (let i = 0; i < ids.length; i += chunkSize) {
              chunks.push(ids.slice(i, i + chunkSize));
            }
            const allDetails = [];
            for (const chunk of chunks) {
              const q = encodeURIComponent(chunk.join(','));
              const det = await axios.get(`https://api.glowytics.com/api/google-ads/meta-creative-details?creativeIds=${q}`, {
                withCredentials: true,
                headers: { 'X-Brand-ID': brandId }
              });
              if (Array.isArray(det?.data?.rows)) {
                allDetails.push(...det.data.rows);
              }
            }
            const map = {};
            allDetails.forEach(d => { if (d?.creative_id) map[d.creative_id] = d; });
            setCreativeDetailsData(allDetails);
            setCreativeDetailsMap(map);
            console.log(`Fetched ${allDetails.length} creative details for ${ids.length} unique creatives`);
          } catch (e) {
            console.warn('Creative details fetch failed; proceeding without media fields', e);
            setCreativeDetailsData([]);
            setCreativeDetailsMap({});
          }

          if (rows.length > 0) {
            // creative_id bazında agregasyon
            const aggregated = {};
            for (const r of rows) {
              const cid = r.creative_id || '(unknown)';
              if (!aggregated[cid]) {
                aggregated[cid] = { spend: 0, clicks: 0, impressions: 0, purchases: 0, revenue: 0 };
              }
              aggregated[cid].spend += Number(r.spend || r.cost || 0);
              aggregated[cid].clicks += Number(r.clicks || 0);
              aggregated[cid].impressions += Number(r.impressions || 0);
              aggregated[cid].purchases += Number(r.conversions || 0);
              aggregated[cid].revenue += Number(r.conversion_value || 0);
            }

            const creativesList = Object.entries(aggregated).map(([cid, agg], i) => {
              const det = (typeof creativeDetailsMap === 'object' && creativeDetailsMap) ? creativeDetailsMap[cid] : undefined;
              const latest = (rows && cid) ? rows.reduce((acc, r) => {
                if (r.creative_id !== cid) return acc;
                if (!acc) return r;
                const rt = r?.date ? new Date(r.date).getTime() : 0;
                const at = acc?.date ? new Date(acc.date).getTime() : -1;
                return rt >= at ? r : acc;
              }, null) : null;
              return {
                id: `cid-${i + 1}`,
                creative_id: cid,
                name: det?.name || `Creative ${cid}`,
                is_video: !!det?.is_video,
                date: null,
                spend: agg.spend,
                clicks: agg.clicks,
                impressions: agg.impressions,
                purchases: agg.purchases,
                revenue: agg.revenue,
                image_url: det?.image_url || null,
                thumbnail_url: det?.thumbnail_url || null,
                best_media_url: det?.best_media_url || det?.enhanced_image_url || det?.image_url || null,
                video_id: det?.video_id || null,
                video_watch_url: det?.video_watch_url || null,
                title: det?.title || null,
                body: det?.body || null,
                object_type: det?.object_type || null,
              ad_id: latest?.ad_id || null,
              ad_name: latest?.ad_name || null,
              campaign_id: latest?.campaign_id || null,
              campaign_name: latest?.campaign_name || null,
              campaignKey: latest?.campaign_id || latest?.campaign_name || 'unknown',
              adGroupId: latest?.adset_id || null,
              adGroupName: latest?.adset_name || null,
              adGroupKey: latest?.adset_id || latest?.adset_name || null,
              platform: (latest?.platform || det?.platform || '').toLowerCase()
              };
            }).sort((a, b) => Number(b.spend) - Number(a.spend));

            setCreatives(creativesList);
            if (creativesList.length > 0) {
              setSelectedCreative(creativesList[0]);
            }
          } else {
            console.warn('No meta ad performance with creative data available.');
            setCreatives([]);
            setSelectedCreative(null);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
          setCreatives([]);
          setSelectedCreative(null);
        }
      } catch (error) {
        console.error('Error in data fetching process:', error);
        // Hata durumunda boş veri kullan
        setCreatives([]);
        setSelectedCreative(null);
      } finally {
        setLoading(false);
      }
    };

    // Fonksiyonu çağır
    fetchData();
  }, [dateRange, customStartDate, customEndDate, selectedAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Facebook video URL'inden video ID'sini çıkaran fonksiyon
  const extractFacebookVideoId = (url) => {
    if (!url) return null;
    
    // URL'de "v=" parametresini arayalım
    const vMatch = url.match(/[?&]v=([^&]+)/);
    if (vMatch && vMatch[1]) return vMatch[1];
    
    // URL'de "/videos/" formatını arayalım
    const videoMatch = url.match(/\/videos\/([0-9]+)/);
    if (videoMatch && videoMatch[1]) return videoMatch[1];
    
    // URL'de "/watch/" formatını arayalım
    const watchMatch = url.match(/\/watch\/\?v=([0-9]+)/);
    if (watchMatch && watchMatch[1]) return watchMatch[1];
    
    // URL'in son kısmı ID olabilir
    const pathParts = url.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];
    if (/^[0-9]+$/.test(lastPart)) return lastPart;
    
    console.log("Video ID çıkarılamadı:", url);
    return null;
  };

  // Büyük sayıları formatla (M, K)
  const formatLargeNumber = (num) => {
    if (num === undefined || num === null) return "0";
    if (num === 0) return "0";
    
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toString();
    }
  };
  
  // Yüzde formatla
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return "0%";
    return `${value.toFixed(1)}%`;
  };

  // Metrikleri hesapla
  const calculateMetrics = () => {
    if (!creatives || creatives.length === 0) {
      return {
        totalImpressions: 0,
        totalClicks: 0,
        totalSpend: 0,
        totalPurchases: 0,
        totalRevenue: 0,
        ctr: 0,
        roas: 0
      };
    }
    
    const totalImpressions = creatives.reduce((sum, item) => sum + Number(item.impressions || 0), 0);
    const totalClicks = creatives.reduce((sum, item) => sum + Number(item.clicks || 0), 0);
    const totalSpend = creatives.reduce((sum, item) => sum + Number(item.spend || 0), 0);
    const totalPurchases = creatives.reduce((sum, item) => sum + Number(item.purchases || 0), 0);
    const totalRevenue = creatives.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
    
    // Hesaplanan metrikler
    const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
    
    // Facebook için ortalama engagement oranı (gösterimi olan kreatiflerde %3-7 arası makuldür)
    // Burada engagement oran hesaplayamadığımız için yaklaşık bir değer kullanıyoruz
    // Normalde engagement = (likes + comments + shares) / impressions
    const engagement = 4.8; // Gerçek veri olmadığı için sabit değer
    
    return {
      totalImpressions,
      totalClicks,
      totalSpend,
      totalPurchases,
      totalRevenue,
      ctr,
      roas,
      engagement
    };
  };

  // Telefon Mockup Bileşeni
  const PhoneMockup = ({ children }) => (
    <div className="relative mx-auto border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl">
      <div className="w-[148px] h-[18px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>
      <div className="h-[32px] w-[3px] bg-gray-800 absolute -start-[17px] top-[72px] rounded-s-lg"></div>
      <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
      <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
      <div className="h-[64px] w-[3px] bg-gray-800 absolute -end-[17px] top-[142px] rounded-e-lg"></div>
      <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white dark:bg-gray-800">
        <div className="w-full h-full overflow-y-auto no-scrollbar">
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 border border-blue-100 shadow-sm rounded-xl">
            <img 
              src="/6033716.png" 
              alt="Meta Ads Logo" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Creative Performance</h1>
            <p className="text-sm text-gray-500 mt-1">Analyze your ad creatives across Meta platforms</p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <AccountSwitcher platform="META_ADS" />
          {metaAccountOptions.length >= 2 && (
            <select
              value={metaAccountFilter}
              onChange={(e) => setMetaAccountFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300 min-w-[160px]"
            >
              <option value="all">All Accounts</option>
              {metaAccountOptions.map(id => (
                <option key={id} value={id}>{accountNameMap[id] || id}</option>
              ))}
            </select>
          )}
          {/* Only Date filter to the left of Export */}
          <div className="hidden lg:flex items-center gap-2">
            <div className="relative">
              <select
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  if (e.target.value === 'custom') setShowCalendar(true);
                  else setShowCalendar(false);
                }}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="thisMonth">This month</option>
                <option value="lastMonth">Last month</option>
                <option value="custom">Custom</option>
              </select>
              {dateRange === 'custom' && customStartDate && customEndDate && (
                <button
                  onClick={() => setShowCalendar(true)}
                  className="ml-1 px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  {customStartDate.toLocaleDateString('tr-TR')} – {customEndDate.toLocaleDateString('tr-TR')}
                </button>
              )}
              {showCalendar && dateRange === 'custom' && (
                <div className="absolute z-50 mt-2 right-0 bg-white border rounded-xl shadow-xl p-4">
                  <DatePicker
                    selected={customStartDate}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setCustomStartDate(start);
                      setCustomEndDate(end);
                      if (start && end) setShowCalendar(false);
                    }}
                    startDate={customStartDate}
                    endDate={customEndDate}
                    selectsRange
                    inline
                    maxDate={new Date()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters moved into header; template creatives hidden by default */}

      {/* Performance Summary Cards - Modernized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Impressions', value: calculateMetrics().totalImpressions, icon: Eye, color: 'blue', prefix: '' },
          { label: 'Revenue', value: calculateMetrics().totalRevenue, icon: BarChart, color: 'emerald', prefix: '₺' },
          { label: 'Spend', value: calculateMetrics().totalSpend, icon: BarChart3, color: 'indigo', prefix: '₺' },
          { label: 'Conversions', value: calculateMetrics().totalPurchases, icon: ShoppingCart, color: 'purple', prefix: '' },
          { label: 'ROAS', value: calculateMetrics().roas.toFixed(2), icon: TrendingUp, color: 'rose', prefix: '', suffix: 'x', isFormatted: true }
        ].map((metric, idx) => (
          <Card key={idx} className="p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl bg-${metric.color}-50`}>
                  <metric.icon className={`w-5 h-5 text-${metric.color}-600`} />
                </div>
                {/* Trend indicator placeholder - can be dynamic later */}
                <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                  Last 30d
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metric.prefix}{metric.isFormatted ? metric.value : formatLargeNumber(metric.value)}{metric.suffix}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Main Layout - Creative List and Preview */}
      <div className="flex flex-col lg:flex-row gap-6 relative">
        {/* Left Column: Creative List */}
        <div className="w-full lg:w-7/12 space-y-6">
          {(campaignList.length > 0 || creativeIdList.length > 0) && (
            <Card className="rounded-xl overflow-hidden mb-4">
              <div className="p-4 border-b bg-white text-gray-800 font-medium flex justify-between items-center">
                {!isMarketer ? (
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => { setListViewMode('campaigns'); setSelectedCreativeIdKey(null); }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${listViewMode === 'campaigns' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Campaigns
                    </button>
                    <button
                      onClick={() => { setListViewMode('creativeIds'); setSelectedCampaignKey(null); setSelectedAdGroupKey(null); }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${listViewMode === 'creativeIds' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Creative IDs
                    </button>
                  </div>
                ) : (
                  <span className="text-sm font-medium">Creative IDs</span>
                )}
                <span className="text-xs text-gray-500">Total: {listViewMode === 'campaigns' ? campaignList.length : creativeIdList.length}</span>
              </div>
              {/* Creative IDs Table */}
              {(listViewMode === 'creativeIds' || isMarketer) && (
              <div className="overflow-x-auto">
                <div className="max-h-[360px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left text-gray-500 font-semibold">
                        <th className="py-2 px-3">Creative ID</th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('spend')}>
                          <span className="inline-flex items-center gap-1">Spend {creativeIdSort.key === 'spend' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('revenue')}>
                          <span className="inline-flex items-center gap-1">Revenue {creativeIdSort.key === 'revenue' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('purchases')}>
                          <span className="inline-flex items-center gap-1">Purch. {creativeIdSort.key === 'purchases' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('clicks')}>
                          <span className="inline-flex items-center gap-1">Clicks {creativeIdSort.key === 'clicks' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('impressions')}>
                          <span className="inline-flex items-center gap-1">Impr. {creativeIdSort.key === 'impressions' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('cpm')}>
                          <span className="inline-flex items-center gap-1">CPM {creativeIdSort.key === 'cpm' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                        <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCreativeIdSort('roas')}>
                          <span className="inline-flex items-center gap-1">ROAS {creativeIdSort.key === 'roas' && <span>{creativeIdSort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedCreativeIds.map((c, idx) => {
                        const roas = Number(c.spend || 0) > 0 ? Number(c.revenue || 0) / Number(c.spend || 0) : 0;
                        const cpm = Number(c.impressions || 0) > 0 ? (Number(c.spend || 0) / Number(c.impressions || 0)) * 1000 : 0;
                        const isSelected = selectedCreativeIdKey === c.creativeId;
                        return (
                          <tr
                            key={c.creativeId || idx}
                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-gray-50'}`}
                            onClick={() => setSelectedCreativeIdKey(c.creativeId)}
                          >
                            <td className="py-2 px-3 text-gray-800 font-medium font-mono text-[11px]" title={c.creativeId}>{c.creativeId}</td>
                            <td className="py-2 px-3 text-right text-gray-700">₺{Math.round(Number(c.spend || 0)).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">₺{Math.round(Number(c.revenue || 0)).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{Number(c.purchases || 0).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{Number(c.clicks || 0).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{Number(c.impressions || 0).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{cpm > 0 ? `₺${cpm.toFixed(2)}` : '-'}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{roas.toFixed(2)}x</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Campaigns Table - hidden for marketers */}
              {listViewMode === 'campaigns' && !isMarketer && (
              <div className="overflow-x-auto">
                <div className="max-h-[360px] overflow-y-auto">
                  <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-500 font-semibold">
                        <th className="py-2 px-2 w-8"></th>
                      <th className="py-2 px-3">Campaign</th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('spend')}>
                        <span className="inline-flex items-center gap-1">
                          Spend
                          {campaignSort.key === 'spend' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('revenue')}>
                        <span className="inline-flex items-center gap-1">
                          Revenue
                          {campaignSort.key === 'revenue' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('purchases')}>
                        <span className="inline-flex items-center gap-1">
                          Purch.
                          {campaignSort.key === 'purchases' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('clicks')}>
                        <span className="inline-flex items-center gap-1">
                          Clicks
                          {campaignSort.key === 'clicks' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('impressions')}>
                        <span className="inline-flex items-center gap-1">
                          Impr.
                          {campaignSort.key === 'impressions' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('cpm')}>
                        <span className="inline-flex items-center gap-1">
                          CPM
                          {campaignSort.key === 'cpm' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                      <th className="py-2 px-3 text-right cursor-pointer select-none" onClick={() => handleCampaignSort('roas')}>
                        <span className="inline-flex items-center gap-1">
                          ROAS
                          {campaignSort.key === 'roas' && <span>{campaignSort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedCampaigns.map((c, idx) => {
                      const roas = Number(c.spend || 0) > 0 ? Number(c.revenue || 0) / Number(c.spend || 0) : 0;
                      const cpm = Number(c.impressions || 0) > 0 ? (Number(c.spend || 0) / Number(c.impressions || 0)) * 1000 : 0;
                      const cKey = c.campaignId || c.campaignName || idx;
                      return (
                        <React.Fragment key={cKey}>
                          <tr
                            className={`hover:bg-gray-50 cursor-pointer ${selectedCampaignKey === (c.campaignId || c.campaignName) ? 'bg-blue-50' : ''}`}
                            onClick={() => setSelectedCampaignKey(c.campaignId || c.campaignName)}
                          >
                            <td className="py-2 px-2 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedCampaigns(prev => ({ ...prev, [cKey]: !prev[cKey] }));
                                }}
                                className="p-1 rounded hover:bg-gray-200"
                              >
                                {expandedCampaigns[cKey] ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
                              </button>
                            </td>
                            <td className="py-2 px-3 text-gray-800 font-medium truncate">{c.campaignName}</td>
                            <td className="py-2 px-3 text-right text-gray-700">₺{Math.round(Number(c.spend || 0)).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">₺{Math.round(Number(c.revenue || 0)).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{Number(c.purchases || 0).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{Number(c.clicks || 0).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{Number(c.impressions || 0).toLocaleString('tr-TR')}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{cpm > 0 ? `₺${cpm.toFixed(2)}` : '-'}</td>
                            <td className="py-2 px-3 text-right text-gray-700">{roas.toFixed(2)}x</td>
                          </tr>
                          {expandedCampaigns[cKey] && adGroupByCampaign[cKey] && adGroupByCampaign[cKey].length > 0 && (
                            adGroupByCampaign[cKey].map(ag => {
                              const active = selectedAdGroupKey === ag.adGroupKey;
                              const roasAg = Number(ag.spend || 0) > 0 ? Number(ag.revenue || 0) / Number(ag.spend || 0) : 0;
                              const cpmAg = Number(ag.impressions || 0) > 0 ? (Number(ag.spend || 0) / Number(ag.impressions || 0)) * 1000 : 0;
                              return (
                                <tr
                                  key={ag.adGroupKey}
                                  className={`bg-gray-50 hover:bg-blue-50 cursor-pointer ${active ? 'bg-blue-50/60' : ''}`}
                                  onClick={() => {
                                    setSelectedCampaignKey(c.campaignId || c.campaignName);
                                    setSelectedAdGroupKey(ag.adGroupKey);
                                  }}
                                >
                                  <td className="py-1.5 px-2 text-center"></td>
                                  <td className="py-1.5 px-3 text-gray-700 truncate">
                                    <span className="font-medium">{ag.adGroupName}</span>
                                  </td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">₺{Math.round(Number(ag.spend || 0)).toLocaleString('tr-TR')}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">₺{Math.round(Number(ag.revenue || 0)).toLocaleString('tr-TR')}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{Number(ag.purchases || 0).toLocaleString('tr-TR')}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{Number(ag.clicks || 0).toLocaleString('tr-TR')}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{Number(ag.impressions || 0).toLocaleString('tr-TR')}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{cpmAg > 0 ? `₺${cpmAg.toFixed(2)}` : '-'}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{roasAg.toFixed(2)}x</td>
                                </tr>
                              );
                            })
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
              )}
            </Card>
          )}
          <Card className="rounded-xl overflow-hidden">
            {/* Başlık kısmı için rengi değiştirdim */}
            <div className="p-4 border-b bg-blue-50 text-blue-700 font-medium flex justify-between items-center">
              <span>Creative List</span>
              {!loading && filteredCreatives.length > 0 && (
                <span className="text-xs text-blue-500">Total: {filteredCreatives.length} creatives</span>
              )}
            </div>
            
            {/* Creative List - normal scroll ile */}
            <div
              id="creative-list-scroll"
              onScroll={(e) => {
                const el = e.currentTarget.querySelector('.scroll-body');
                if (!el) return;
                const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;
                if (nearBottom) {
                  setVisibleThumbCount((c) => Math.min(c + 20, filteredCreatives.length));
                }
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              ) : !selectedCampaignKey ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="text-gray-400 mb-2">
                    <Image className="w-12 h-12 mx-auto mb-2" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-1">Select a campaign first</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">
                    Choose a campaign from the list above to see its creatives.
                  </p>
                </div>
              ) : filteredCreatives.length > 0 ? (
                <div className="scroll-body max-h-[600px] overflow-y-auto">
                  <div className="grid grid-cols-1 gap-2 p-3">
                    {filteredCreatives.slice(0, 500).map((creative, index) => (
                      <div
                        key={creative.creative_id || index}
                        className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          selectedCreative?.creative_id === creative.creative_id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setSelectedCreative(creative);
                          // If cached, apply immediately to avoid flicker
                          const cid = creative?.creative_id;
                          if (cid && previewCache[cid]) {
                            setPreviewHtml(previewCache[cid]);
                            setPreviewLoading(false);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {/* Thumbnail (proxy üzerinden küçük preview, lazy-load in 20s) */}
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            {(() => {
                              const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : '';
                              const useProxy = index < visibleThumbCount;
                              const proxy =
                                useProxy && creative?.creative_id && brandId
                                  ? `https://api.glowytics.com/api/meta-ads/live/creative-thumbnail?creativeId=${encodeURIComponent(
                                      creative.creative_id
                                    )}&brandId=${encodeURIComponent(brandId)}&w=120&h=120`
                                  : (creative.best_media_url || creative.thumbnail_url || creative.image_url || "/glowytics-frontpage/public/Post1212112.png");
                              return (
                                <img
                                  src={proxy}
                                  alt={creative.title || "Creative"}
                                  className="w-full h-full object-cover"
                                  onLoad={(e) => {
                                    // If proxy returned tiny transparent fallback, switch to direct media url
                                    const img = e.currentTarget;
                                    if ((img.naturalWidth <= 1 && img.naturalHeight <= 1) || img.complete && img.naturalWidth === 0) {
                                      img.src =
                                        creative.best_media_url ||
                                        creative.thumbnail_url ||
                                        creative.image_url ||
                                        FALLBACK_IMG;
                                    }
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      creative.best_media_url ||
                                      creative.thumbnail_url ||
                                      creative.image_url ||
                                      FALLBACK_IMG;
                                  }}
                                />
                              );
                            })()}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="min-w-0">
                                <h4 className="font-medium text-sm truncate">
                                  {creative.ad_name || creative.title || creative.name || 'Untitled Creative'}
                                </h4>
                                <div className="text-[10px] text-gray-500 font-mono truncate">
                                  {creative.creative_id ? `ID: ${creative.creative_id}` : ''}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {creative.platform && (
                                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                                    {creative.platform}
                                  </span>
                                )}
                                {creative.is_video ? (
                                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">
                                    Video
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-xs">
                                    Image
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-5 gap-2 text-xs text-gray-600">
                              <div>
                                <span className="text-gray-500">Spend:</span>{' '}
                                ₺{Math.round(Number(creative.spend || 0)).toLocaleString('tr-TR')}
                              </div>
                              <div>
                                <span className="text-gray-500">Revenue:</span>{' '}
                                ₺{Math.round(Number(creative.revenue || 0)).toLocaleString('tr-TR')}
                              </div>
                              <div>
                                <span className="text-gray-500">Clicks:</span>{' '}
                                {creative.clicks || 0}
                              </div>
                              <div>
                                <span className="text-gray-500">CTR:</span>{' '}
                                {(() => {
                                  const impressions = Number(creative.impressions || 0);
                                  const clicks = Number(creative.clicks || 0);
                                  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                                  return `${ctr.toFixed(2)}%`;
                                })()}
                              </div>
                              <div>
                                <span className="text-gray-500">ROAS:</span>{' '}
                                {(() => {
                                  const spend = Number(creative.spend || 0);
                                  const revenue = Number(creative.revenue || 0);
                                  const roas = spend > 0 ? revenue / spend : 0;
                                  return `${roas.toFixed(1)}x`;
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {(() => {
                      const filteredCreatives = creatives.filter(creative => {
                        if (hasTemplateVariables(creative)) return false;
                        if (selectedPlatform !== 'all' && (creative.platform || '').toLowerCase() !== selectedPlatform) return false;
                        if (selectedFormat !== 'all') {
                          const isVideo = !!creative.is_video;
                          if (selectedFormat === 'video' && !isVideo) return false;
                          if (selectedFormat === 'single' && isVideo) return false;
                        }
                        return true;
                      });
                      
                      return filteredCreatives.length > 500 && (
                        <div className="p-3 text-center bg-amber-50 text-amber-700 text-sm">
                          {filteredCreatives.length - 500} more creatives available but not shown for performance
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="text-gray-400 mb-2">
                    <Image className="w-12 h-12 mx-auto mb-2" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">
                    Select a creative from the list to view detailed performance data.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
        
        {/* Creative Preview - Sabit pozisyonlama */}
        <div className="w-5/12">
          <div className="sticky top-6">
            <div className="mb-4">
              {selectedCreative ? (
                <div>
                  {/* ROAS Section removed as requested */}

                  {/* Content Container - cardsız/arkaplan yok */}
                  <div>
                    {/* Ad Preview (Graph API) öncelikli; yoksa video/image fallback */}
                    <div className="relative max-w-full">
                      {previewLoading ? (
                        <div className="w-full flex items-center justify-center py-20 text-sm text-gray-500">Loading preview…</div>
                      ) : (previewHtml ? (
                        (() => {
                          let html = previewHtml
                            .replace(/scrolling\s*=\s*["']yes["']/gi, 'scrolling="no"')
                            .replace(/height\s*=\s*["']\d+["']/gi, 'height="2000"');
                          return (
                            <div
                              ref={previewContainerRef}
                              className="w-full preview-noscroll overflow-hidden rounded-2xl border border-gray-100 shadow-sm flex justify-center"
                              style={{ maxHeight: 850 }}
                              dangerouslySetInnerHTML={{ __html: `<style>.preview-noscroll iframe{width:100%!important;height:2000px!important;border:none!important;overflow:hidden!important;}</style>${html}` }}
                            />
                          );
                        })()
                      ) : selectedCreative.is_video ? (
                        <div className="w-full">
                          {/* Facebook video için embed iframe */}
                          {(selectedCreative.video_watch_url && selectedCreative.video_watch_url.includes('facebook.com')) ? (
                            <iframe
                              ref={videoIframeRef}
                              src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(selectedCreative.video_watch_url)}&show_text=false&width=560&height=315&appId&autoplay=false&mute=1`}
                              width="100%"
                              height="500"
                              style={{ border: 'none', overflow: 'hidden' }}
                              scrolling="no"
                              frameBorder="0"
                              allowFullScreen={true}
                              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                              onLoad={() => {
                                // iframe yüklendiğinde çalışacak
                                console.log('Facebook video iframe yüklendi');
                                if (videoIframeRef.current) {
                                  try {
                                    // İframe yüklendikten sonra JavaScript ile sesi kapatalım
                                    const message = {
                                      method: 'setVolume',
                                      value: 0  // Ses kapalı
                                    };
                                    videoIframeRef.current.contentWindow.postMessage(JSON.stringify(message), '*');
                                  } catch (e) {
                                    console.error('Video iframe ses ayarı yapılamadı:', e);
                                  }
                                }
                              }}
                            ></iframe>
                          ) : selectedCreative.video_id ? (
                            <iframe
                              ref={videoIframeRef}
                              src={`https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fwatch%2F%3Fv%3D${selectedCreative.video_id}&show_text=false&width=560&height=315&appId&autoplay=false&mute=1`}
                              width="100%"
                              height="500"
                              style={{ border: 'none', overflow: 'hidden' }}
                              scrolling="no"
                              frameBorder="0"
                              allowFullScreen={true}
                              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                              onLoad={() => {
                                console.log('Facebook video iframe yüklendi (video_id)');
                                if (videoIframeRef.current) {
                                  try {
                                    const message = {
                                      method: 'setVolume',
                                      value: 0  // Ses kapalı
                                    };
                                    videoIframeRef.current.contentWindow.postMessage(JSON.stringify(message), '*');
                                  } catch (e) {
                                    console.error('Video iframe ses ayarı yapılamadı:', e);
                                  }
                                }
                              }}
                            ></iframe>
                          ) : (selectedCreative.video_permalink_url || selectedCreative.enhanced_image_url)?.includes('facebook.com') ? (
                            <iframe
                              ref={videoIframeRef}
                              src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(selectedCreative.video_permalink_url || selectedCreative.enhanced_image_url)}&show_text=false&width=560&height=315&appId&autoplay=false&mute=1`}
                              width="100%"
                              height="500"
                              style={{ border: 'none', overflow: 'hidden' }}
                              scrolling="no"
                              frameBorder="0"
                              allowFullScreen={true}
                              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                              onLoad={() => {
                                console.log('Facebook video iframe yüklendi (permalink)');
                                if (videoIframeRef.current) {
                                  try {
                                    const message = {
                                      method: 'setVolume',
                                      value: 0  // Ses kapalı
                                    };
                                    videoIframeRef.current.contentWindow.postMessage(JSON.stringify(message), '*');
                                  } catch (e) {
                                    console.error('Video iframe ses ayarı yapılamadı:', e);
                                  }
                                }
                              }}
                            ></iframe>
                          ) : selectedCreative.best_media_url?.includes('facebook.com') ? (
                            // URL'den video ID'si çıkarmaya çalışıyoruz, başarılıysa iframe kullanıyoruz
                            (() => {
                              const videoId = extractFacebookVideoId(selectedCreative.best_media_url);
                              if (videoId) {
                                return (
                                  <iframe
                                    ref={videoIframeRef}
                                    src={`https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fwatch%2F%3Fv%3D${videoId}&show_text=false&width=560&height=315&appId&autoplay=false&mute=1`}
                                    width="100%"
                                    height="500"
                                    style={{ border: 'none', overflow: 'hidden' }}
                                    scrolling="no"
                                    frameBorder="0"
                                    allowFullScreen={true}
                                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                    onLoad={() => {
                                      console.log('Facebook video iframe yüklendi (extracted ID)');
                                      if (videoIframeRef.current) {
                                        try {
                                          const message = {
                                            method: 'setVolume',
                                            value: 0  // Ses kapalı
                                          };
                                          videoIframeRef.current.contentWindow.postMessage(JSON.stringify(message), '*');
                                        } catch (e) {
                                          console.error('Video iframe ses ayarı yapılamadı:', e);
                                        }
                                      }
                                    }}
                                  ></iframe>
                                );
                              } else {
                                console.log("URL'den video ID çıkarılamadı:", selectedCreative.best_media_url);
                                return (
                                  <div className="flex flex-col items-center justify-center py-20 bg-gray-800 text-white p-4 rounded-lg">
                                    <p className="text-sm text-center mb-2">Video embed edilemiyor. URL'yi tarayıcıda açın:</p>
                                    <a 
                                      href={selectedCreative.best_media_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-300 hover:underline truncate max-w-full"
                                    >
                                      {selectedCreative.best_media_url}
                                    </a>
                                  </div>
                                );
                              }
                            })()
                          ) : (
                            <video 
                              className="w-full object-contain"
                              controls
                              autoPlay
                              muted
                              src={selectedCreative.video_watch_url || selectedCreative.best_media_url} 
                              poster={selectedCreative.thumbnail_url || selectedCreative.image_url}
                              onError={(e) => {
                                console.error("Video yüklenemedi:", e);
                                console.log("Video URL:", selectedCreative.video_watch_url || selectedCreative.best_media_url);
                                console.log("Mevcut Video Alan Değerleri:", {
                                  video_id: selectedCreative.video_id,
                                  video_watch_url: selectedCreative.video_watch_url,
                                  video_permalink_url: selectedCreative.video_permalink_url,
                                  best_media_url: selectedCreative.best_media_url
                                });
                                
                                // Fallback olarak resim göster
                                e.target.style.display = 'none';
                                const fallbackImage = document.createElement('img');
                                fallbackImage.src = selectedCreative.thumbnail_url || selectedCreative.image_url || FALLBACK_IMG;
                                fallbackImage.className = "w-full object-contain";
                                e.target.parentNode.appendChild(fallbackImage);
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        <img 
                          src={selectedCreative.best_media_url || selectedCreative.thumbnail_url || selectedCreative.image_url || FALLBACK_IMG} 
                          alt={selectedCreative.title || "Creative preview"}
                          className="w-full object-contain" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = FALLBACK_IMG;
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <p>Select a creative to view details</p>
                </div>
              )}
            </div>
            {/* Details card separated from preview */}
            {selectedCreative && (
              <div className="mt-4">
                <Card className="rounded-xl overflow-hidden">
                  <div className="p-3 border-b bg-white text-purple-700 font-medium rounded-t-xl">
                    Creative Details
                  </div>
                  <div className="p-3 max-w-full overflow-x-auto">
                    {/* Başlık (yalnızca ad name göster) */}
                    <div className="mb-2">
                      <h3 className="font-medium text-sm">
                        {selectedCreative.ad_name || selectedCreative.title || selectedCreative.name || 'Untitled Creative'}
                      </h3>
                    </div>
                    {/* Creative body metni varsa göster */}
                    {selectedCreative.body && (
                      <div className="mb-3 p-2 bg-gray-50 rounded text-xs text-gray-700">
                        {selectedCreative.body}
                      </div>
                    )}
                    {/* ID/detay blokları kaldırıldı */}
                    {/* Performance Metrics */}
                    <div className="grid grid-cols-4 gap-2 mb-2 text-xs">
                      <div>
                        <span className="text-gray-500">Spend:</span>{' '}
                        <span className="font-medium">₺{Math.round(Number(selectedCreative.spend || 0)).toLocaleString('tr-TR')}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Clicks:</span>{' '}
                        <span className="font-medium">{selectedCreative.clicks}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">CPC:</span>{' '}
                        <span className="font-medium">
                          ₺{(() => {
                            const spend = Number(selectedCreative.spend || 0);
                            const clicks = Number(selectedCreative.clicks || 0);
                            const cpc = clicks > 0 ? spend / clicks : 0;
                            return cpc.toFixed(2);
                          })()}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">CTR:</span>{' '}
                        <span className="font-medium">
                          {(() => {
                            const impressions = Number(selectedCreative.impressions || 0);
                            const clicks = Number(selectedCreative.clicks || 0);
                            const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                            return `${ctr.toFixed(2)}%`;
                          })()}
                        </span>
                      </div>
                    </div>
                    {/* Performance Trend */}
                    <div className="bg-blue-50 border border-blue-100 p-2 rounded-lg">
                      <h4 className="text-xs font-medium text-blue-800 mb-1 flex items-center gap-1">
                        <LineChart className="w-3 h-3" />
                        Performance Trend{chartSeries.monthly ? ' (Monthly)' : ''}
                      </h4>
                      {chartSeries.data && chartSeries.data.length > 0 ? (
                        <>
                          <div className="h-40 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsLineChart
                                data={chartSeries.data}
                                margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                                <XAxis
                                  dataKey="date"
                                  tick={{ fontSize: 9 }}
                                  tickFormatter={(value) => {
                                    const date = new Date(value);
                                    return chartSeries.monthly
                                      ? date.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' })
                                      : `${date.getDate()}/${date.getMonth()+1}`;
                                  }}
                                />
                                <YAxis 
                                  yAxisId="left" 
                                  orientation="left" 
                                  tick={{ fontSize: 9 }}
                                  allowDecimals={false}
                                  tickFormatter={(value) => Number(value || 0).toLocaleString('tr-TR')}
                                  stroke="#3b82f6"
                                >
                                  <Label value="Spend" position="insideLeft" angle={-90} style={{ textAnchor: 'middle', fontSize: 9, fill: '#3b82f6' }} offset={-5} />
                                </YAxis>
                                <YAxis 
                                  yAxisId="right" 
                                  orientation="right" 
                                  tick={{ fontSize: 9 }}
                                  allowDecimals={false}
                                  tickFormatter={(value) => `₺${Math.round(Number(value || 0)).toLocaleString('tr-TR')}`}
                                  stroke="#16a34a"
                                >
                                  <Label value="Revenue" position="insideRight" angle={90} style={{ textAnchor: 'middle', fontSize: 9, fill: '#16a34a' }} offset={-5} />
                                </YAxis>
                                <Tooltip
                                  formatter={(value, name) => {
                                    if (name === 'spend') return [`₺${Math.round(Number(value || 0)).toLocaleString('tr-TR')}`, 'Spend'];
                                    if (name === 'revenue') return [`₺${Math.round(Number(value || 0)).toLocaleString('tr-TR')}`, 'Revenue'];
                                    if (name === 'clicks') return [Number(value || 0).toLocaleString('tr-TR'), 'Clicks'];
                                    if (name === 'impressions') return [Number(value || 0).toLocaleString('tr-TR'), 'Impressions'];
                                    return [value, name];
                                  }}
                                  labelFormatter={(label) => {
                                    const date = new Date(label);
                                    return chartSeries.monthly
                                      ? date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })
                                      : date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' });
                                  }}
                                />
                                <Legend verticalAlign="top" height={20} iconSize={8} iconType="circle" formatter={(value) => <span style={{ fontSize: 9, color: '#4b5563', textTransform: 'uppercase' }}>{value}</span>} />
                                <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="SPEND" />
                                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="REVENUE" />
                              </RechartsLineChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="mt-1 text-xs text-blue-700 flex justify-between">
                            <div>
                              <span className="font-medium">Total Spend:</span>{' '}
                              ₺{chartSeries.data.reduce((sum, item) => sum + item.spend, 0).toLocaleString('tr-TR')}
                            </div>
                            <div>
                              <span className="font-medium">Total Revenue:</span>{' '}
                              ₺{chartSeries.data.reduce((sum, item) => sum + (item.revenue || 0), 0).toLocaleString('tr-TR')}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="text-gray-400 mb-2">
                            <LineChart className="w-8 h-8 mx-auto mb-2" />
                          </div>
                          <h3 className="text-sm font-medium text-gray-700 mb-1">No data available</h3>
                          <p className="text-xs text-gray-500 text-center max-w-md">
                            No performance trend data found for this creative.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Report Module - only for allowed brands */}
      {CREATIVE_EMAIL_BRAND_IDS.includes(selectedBrand?.id) && (
        <Card className="rounded-xl overflow-hidden mt-6">
          <div className="p-4 border-b bg-white">
            <div className="flex items-center gap-2 text-gray-800 font-medium">
              <Mail className="w-4 h-4" />
              <span>Send Creative Performance Report</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Top creative'leri thumbnail ile birlikte e-posta olarak gönderin</p>
          </div>
          <div className="p-4 bg-gray-50/50 space-y-3">
            {/* Date range selector */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Tarih Aralığı:</span>
              {[
                { key: 'last7', label: 'Son 7 Gün' },
                { key: 'last30', label: 'Son 30 Gün' },
                { key: 'last90', label: 'Son 90 Gün' },
                { key: 'custom', label: 'Özel' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => { setEmailDatePreset(p.key); setEmailStatus(null); }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    emailDatePreset === p.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {emailDatePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <DatePicker
                  selected={emailCustomStart}
                  onChange={d => { setEmailCustomStart(d); setEmailStatus(null); }}
                  selectsStart
                  startDate={emailCustomStart}
                  endDate={emailCustomEnd}
                  maxDate={new Date()}
                  placeholderText="Başlangıç"
                  dateFormat="dd/MM/yyyy"
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg w-28 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-400 text-xs">&rarr;</span>
                <DatePicker
                  selected={emailCustomEnd}
                  onChange={d => { setEmailCustomEnd(d); setEmailStatus(null); }}
                  selectsEnd
                  startDate={emailCustomStart}
                  endDate={emailCustomEnd}
                  minDate={emailCustomStart}
                  maxDate={new Date()}
                  placeholderText="Bitiş"
                  dateFormat="dd/MM/yyyy"
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg w-28 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            {/* Email + Send */}
            <div className="flex items-center gap-3">
              <input
                type="email"
                placeholder="recipient@example.com"
                value={reportEmail}
                onChange={(e) => { setReportEmail(e.target.value); setEmailStatus(null); }}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={sendingEmail}
              />
              <button
                onClick={async () => {
                  if (!reportEmail || !reportEmail.includes('@')) {
                    setEmailStatus({ type: 'error', message: 'Lütfen geçerli bir e-posta adresi girin.' });
                    return;
                  }
                  if (emailDatePreset === 'custom' && (!emailCustomStart || !emailCustomEnd)) {
                    setEmailStatus({ type: 'error', message: 'Lütfen başlangıç ve bitiş tarihini seçin.' });
                    return;
                  }
                  setSendingEmail(true);
                  setEmailStatus(null);
                  try {
                    const brandId = localStorage.getItem('currentBrandId');
                    const today = new Date();
                    const fmt = d => d.toISOString().slice(0, 10);
                    let startDate, endDate = fmt(today);
                    if (emailDatePreset === 'last7') {
                      const s = new Date(today); s.setDate(s.getDate() - 7); startDate = fmt(s);
                    } else if (emailDatePreset === 'last30') {
                      const s = new Date(today); s.setDate(s.getDate() - 30); startDate = fmt(s);
                    } else if (emailDatePreset === 'last90') {
                      const s = new Date(today); s.setDate(s.getDate() - 90); startDate = fmt(s);
                    } else {
                      startDate = fmt(emailCustomStart);
                      endDate = fmt(emailCustomEnd);
                    }
                    const res = await fetch('https://api.glowytics.com/api/email/send-creative-performance', {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
                      body: JSON.stringify({ brandId, email: reportEmail, startDate, endDate })
                    });
                    const data = await res.json();
                    if (data.success) {
                      setEmailStatus({ type: 'success', message: `Rapor ${reportEmail} adresine gönderildi (${data.creativesCount} creative, ${data.thumbnailsCount} thumbnail)` });
                      setReportEmail('');
                    } else {
                      setEmailStatus({ type: 'error', message: data.error || 'E-posta gönderilemedi.' });
                    }
                  } catch (e) {
                    setEmailStatus({ type: 'error', message: e.message || 'Ağ hatası.' });
                  } finally {
                    setSendingEmail(false);
                  }
                }}
                disabled={sendingEmail || !reportEmail}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sendingEmail || !reportEmail
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {sendingEmail ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor...</>
                ) : (
                  <><Send className="w-4 h-4" /> Rapor Gönder</>
                )}
              </button>
            </div>
            {emailStatus && (
              <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${
                emailStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {emailStatus.message}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}